import React, { memo, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useDidUpdateEffect } from '../../../hooks';

import { Text, View, Image, Pressable, StyleSheet, Dimensions } from 'react-native';
import { COLOR, FONT, THEME, __WEB__, __IOS__ } from '../../../constants/styles.js';
import { REQUESTING, SUCCESS } from '../../../actions/statusTypes.js';
import { I18n, Container, Navigation, FormFooter, CTAButton } from '../../components/index.js';

import moment from 'moment';
import momentTz from 'moment-timezone';

import {
  EVENT_TYPE_PHA,
  EVENT_TYPE_PHA_FLY,
  EVENT_TYPE_OHE,
  EVENT_TYPE_SHPE,
  EVENT_TYPE_LAB,
  EVENT_TYPES_REQUIRING_APPROVAL,
  EVENT_TYPE_SECURITY_FORCES_PCM,
  EVENT_TYPE_CMR,
  EVENT_TYPE_CMR_FLY,
  EVENT_TYPE_ANAM_TEST,
  EVENT_TYPE_IMAGING_IMPORT_PICKUP,
  DIRECT_APPOINTMENT_TYPES,
  EVENT_TYPE_COMMUNITY_HEALTH_INTERVIEW,
  EVENT_TYPE_TRAVEL_MEDICINE,
  EVENT_TYPE_SPECIALTY_HEARING,
  EVENT_TYPE_AUDIOGRAM,
} from '../../../constants/eventTypes.js';
import { ROLE_TYPE_PATIENT } from '../../../constants/roleTypes.js';
import { TRAVIS, MACDILL } from '../../../constants/base_keys';
import { ACTIVE_DUTY, RESERVE_DUTY, CIVILIAN_STAFF } from '../../../constants/patientRoles';

import PersonForm from '../../cases/airforce/person_form.js';
import { appointmentLabelForType, AppointmentSelectedTimeSlot } from './common_views.js';
import AppointmentPreReqs, { eventTypeHasPrereqs } from './appointment_prereqs.js';
import StaticInfo from './readonly_request_info.js';

const VERTICAL_LAYOUT_WIDTH = 1000;
const VERTICAL_LAYOUT_WIDTH_HEADER = 850;
const PHA_PHONE_TIP = {
  phone: 'This is the phone number for the Provider to reach you for your PHA.',
};
const AUDIOGRAM_TIP_MACDILL = {
  audiogramType:
    'Select the type of audiogram. If you are unsure, please contact Public Health at (813) 827-9601.',
};

function fixupFormData(formData) {
  if (!formData) formData = {};
  return {
    firstName: formData.firstName || formData.name?.split(' ')[0] || null,
    lastName: formData.lastName || formData.name?.split(' ').slice(1).join(' ') || null,
    email: formData.email || null,
    phone: formData.phone || null,
    group: formData.squadron || formData.group || null,
    rank: formData.rank || null,
    serviceBranch: formData.serviceBranch || formData.service_branch || null,
    status: formData.status || formData.patientRole || null,
    dodId: formData.dodId || formData.dod_id || null,
    department: formData.department || formData.employer_department_id || null,
    imr: formData.imr || null,
    appointmentReason: formData.appointmentReason || null,
    audiogramType: formData.audiogramType || null,
    upcomingApptDate: formData.upcomingApptDate || null,
    updatedAt: formData.updatedAt || null,
    requestedAt: formData.requestedAt || null,
    createdByEmail: formData.createdByEmail || null,
    confirmedAt: formData.confirmedAt || null,
  };
}
function getLocation(eventType, eventTypeDefaults) {
  return (eventType && eventTypeDefaults?.[eventType]?.location) || '';
}
function openSelectCase() {
  Navigation.showModal({
    screen: 'SelectCaseEmployer',
    title: I18n.t('case.activeCases'),
    passProps: {
      includeEmployerSelection: false,
      changeEditingCase: true,
    },
  });
}

const AppointmentRequestForm = memo((props) => {
  const { width } = Dimensions.get('window');
  const [isVerticalLayout, setIsVerticalLayout] = React.useState(width < VERTICAL_LAYOUT_WIDTH);
  const [isVerticalLayoutHeader, setIsVerticalLayoutHeader] = React.useState(
    width < VERTICAL_LAYOUT_WIDTH_HEADER
  );
  const formRef = useRef(null);

  const handleDimsChange = useCallback(({ window: { width } }) => {
    setIsVerticalLayout(width < VERTICAL_LAYOUT_WIDTH);
    setIsVerticalLayoutHeader(width < VERTICAL_LAYOUT_WIDTH_HEADER);
  }, []);
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', handleDimsChange);
    return () => {
      subscription.remove();
    };
  }, []);

  const [submitSuccess, setSubmitSuccess] = useState(false);
  useDidUpdateEffect(() => {
    if (props.status === SUCCESS || props.aptsRequestUpdateStatus === SUCCESS) {
      setSubmitSuccess(true);
    }
  }, [props.status, props.aptsRequestUpdateStatus]);

  // const [preReqsValid, setPreReqsValid] = useState(false);
  const [preReqsValid, setPreReqsValid] = useState(
    !!props.admin || !eventTypeHasPrereqs(props.eventType)
  );
  const [data, setData] = React.useState({});
  const [validationError, setValidationError] = React.useState(null);

  const normalizedFormData = useMemo(() => {
    return fixupFormData(props.formData || props.formDefaults);
  }, [props.formData]);
  const normalizedReadonlyData = useMemo(() => {
    const normalized = fixupFormData(props.readOnlyData || {});
    const readonlyData = Object.keys(normalized).reduce((acc, key) => {
      if (normalized[key]) acc[key] = normalized[key];
      return acc;
    }, {});
    return readonlyData;
  }, [props.readOnlyData]);

  const handleDataChange = useCallback(
    (data) => {
      setData(data);
      props.onDataChange?.(data);
    },
    [props.onDataChange]
  );

  function submit() {
    const submitObj = formRef.current?.getObjectToSubmit();
    if (!submitObj) {
      setValidationError('Please fill out all required fields');
      return;
    }
    setValidationError(null);
    const aptDate = new Date(props.selectedDate);
    const timeStrs = props.selectedTimes[0].split(':');
    const appointmentTime = `${moment(aptDate).format('YYYY-MM-DD')}T${timeStrs[0]}:${
      timeStrs[1]
    }:00`;
    const selectedDaysStr = moment(aptDate).isoWeekday();
    const schedule = props.schedules.find(
      (s) => s.schedule_type === props.eventType && (s.days || []).includes(selectedDaysStr)
    );
    const duration = schedule?.duration;

    const dataToSubmit = {
      ...submitObj,
      dod_id: normalizedReadonlyData.dodId || submitObj.dod_id,
      service_branch: normalizedReadonlyData.serviceBranch || submitObj.service_branch,
      ...normalizedReadonlyData,
      employerKeyOrId: `${props.employerId || props.base}`,
      appointmentType: props.eventType,
      appointmentTime,
    };
    delete dataToSubmit.name;
    if (duration) dataToSubmit.duration = duration;

    props.onSubmit(dataToSubmit);
  }

  function cancel() {
    Navigation.dismissAllModals();
  }

  function renderHeaderDateTimeLoc() {
    const location = getLocation(props.eventType, props.eventTypeDefaults);
    return (
      <View style={__WEB__ ? { width: '100%' } : { width: '100%' }}>
        <AppointmentSelectedTimeSlot
          date={props.selectedDate}
          time={props.selectedTimes?.join(', ') || ''}
          eventType={props.eventType}
          location={location}
          isVerticalLayout={isVerticalLayoutHeader}
          deploymentDeets={props.request?.deploymentDeets}
        />
      </View>
    );
  }
  function renderHeaderPatientLookup() {
    if (!props.admin || props.readOnly || props.readOnlyUnlessMissing || submitSuccess) return null;
    return (
      <View style={[THEME.basicRow, { flex: 1, paddingLeft: 25, maxWidth: 480 }]}>
        <Text style={THEME.textBold}>Patient Lookup: </Text>
        <Pressable style={styles.lookupButton} onPress={openSelectCase}>
          <Image
            style={styles.searchIcon}
            source={require('../../../../resources/icon-search.png')}
          />
          <Text style={styles.lookupTxt}>{props.patient?.fullName || 'Find Patient...'}</Text>
        </Pressable>
      </View>
    );
  }

  function renderHeaderPrereqs() {
    if (submitSuccess) return null;
    if (props.admin) return null;
    if (!eventTypeHasPrereqs(props.eventType)) return null;
    return (
      <AppointmentPreReqs
        eventType={props.eventType}
        onPreReqsChecked={setPreReqsValid}
        isVerticalLayout={isVerticalLayoutHeader}
        preChecked={[EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY].includes(props.eventType)}
      />
    );
  }

  function renderHeaderConfirmedBy() {
    if (normalizedFormData?.confirmedAt) {
      const confirmedAt = momentTz(normalizedFormData.confirmedAt)
        .tz(props.timezone)
        .format('MM/DD/YYYY @ HH:mm');
      if (DIRECT_APPOINTMENT_TYPES.includes(props.eventType)) {
        return (
          <View>
            <Text style={THEME.textBold}>Requested On:</Text>
            <Text style={[THEME.textMedium, { marginTop: 5 }]}>{confirmedAt}</Text>
          </View>
        );
      } else if (normalizedFormData.createdByEmail) {
        return (
          <View>
            <Text style={THEME.textBold}>Confirmed By: </Text>
            <Text style={[THEME.textRegular, { marginTop: 0 }]}>
              {normalizedFormData.createdByEmail}
            </Text>
            <Text style={[THEME.textMedium, { fontSize: 14, marginTop: 5 }]}>{confirmedAt}</Text>
          </View>
        );
      }
    }

    if (normalizedFormData?.requestedAt) {
      const requestedAt = momentTz(normalizedFormData.requestedAt)
        .tz(props.timezone)
        .format('MM/DD/YYYY @ HH:mm');
      return (
        <View>
          <Text style={THEME.textBold}>Requested On:</Text>
          <Text style={[THEME.textMedium, { marginTop: 5 }]}>{requestedAt}</Text>
        </View>
      );
    }

    return null;
  }
  const headerPrereqs = renderHeaderPrereqs();
  const headerPatientLookup = renderHeaderPatientLookup();
  const headerConfirmedBy = renderHeaderConfirmedBy();

  function renderHeader() {
    return (
      <View
        style={[
          styles.headerContainer,
          { flexDirection: isVerticalLayoutHeader ? 'column' : 'row' },
        ]}
      >
        <View
          style={[
            styles.headerLeft,
            !isVerticalLayoutHeader
              ? {
                  flex: 1,
                  paddingHorizontal: 25,
                  paddingVertical: 10,
                  paddingHorizontal: 10,
                  height: 110,
                }
              : { paddingVertical: 10, paddingHorizontal: 10, height: 110 },
          ]}
        >
          {renderHeaderDateTimeLoc()}
        </View>
        {(!isVerticalLayoutHeader ||
          !!headerPrereqs ||
          !!headerPatientLookup ||
          !!headerConfirmedBy) && (
          <View
            style={[
              styles.headerRight,
              !isVerticalLayoutHeader
                ? { flex: 1, borderLeftWidth: 1, paddingRight: 40 }
                : { borderTopWidth: 1, paddingTop: 15 },
            ]}
          >
            {headerPrereqs}
            {headerPatientLookup}
            {headerConfirmedBy}
          </View>
        )}
      </View>
    );
  }

  function renderSuccessAdmin(selectedDate, selectedTimes) {
    return (
      <>
        <Text style={THEME.textRegular}>
          {appointmentLabelForType(props.eventType)} Appointment scheduled for{' '}
          <Text style={THEME.textBold}>
            {selectedTimes[0]}, {moment(selectedDate).format('MMMM D, YYYY')}.
          </Text>{' '}
        </Text>
        <Text style={[THEME.textRegular, { marginTop: 40 }]}>
          An email and text message will be sent to the airman to let them know their appointment is
          confirmed
        </Text>
      </>
    );
  }

  function renderSuccessPatient(selectedDate, selectedTimes, eventType) {
    const isRequest = EVENT_TYPES_REQUIRING_APPROVAL.includes(eventType);
    const isChecklist = eventType === EVENT_TYPE_OHE || eventType === EVENT_TYPE_SHPE;
    const isOHE = eventType === EVENT_TYPE_OHE;
    const isCMR = [EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY].includes(eventType);
    const isCommunityHealthInterviewOrTravelMedicine = [
      EVENT_TYPE_COMMUNITY_HEALTH_INTERVIEW,
      EVENT_TYPE_TRAVEL_MEDICINE,
    ].includes(eventType);
    const isAnamTest = EVENT_TYPE_ANAM_TEST === eventType;
    const isImagingImport = EVENT_TYPE_IMAGING_IMPORT_PICKUP === eventType;
    const checklistLabel = isChecklist ? `you've completed your checklist` : 'available';
    const label1 = isRequest
      ? `If ${checklistLabel}, you will be confirmed for `
      : 'You are confirmed for ';
    const formattedDate = (
      <Text style={THEME.textBold}>
        {selectedTimes}, {moment(selectedDate).format('MMMM D, YYYY')}
      </Text>
    );
    const endFirstParagraph = `You will get an email and text notification.`;

    return (
      <>
        {!!isCMR && (
          <Text style={[THEME.textRegular, { marginBottom: 25, textAlign: 'center' }]}>
            Your CMR appointment request has been received. Once reviewed, you will receive another
            message either confirming or rejecting this appointment request. Please allow up to 5
            business days for appointment confirmation. Thank you for your understanding and please
            stay tuned.
          </Text>
        )}
        <Text style={[THEME.textRegular, { textAlign: 'center' }]}>
          {!!isImagingImport ? (
            <>
              {`If you've dropped off your disc, you will be confirmed for `}
              {formattedDate}
              {` to pick it up after your disc has been imported. `}
              {endFirstParagraph}
            </>
          ) : (
            <>
              {label1}
              {formattedDate}. {endFirstParagraph}
            </>
          )}
        </Text>
        {!!isCMR && (
          <Text style={[THEME.textRegular, { marginTop: 25, textAlign: 'center' }]}>
            Please arrive at <Text style={THEME.textBold}>07:30</Text> at the{' '}
            <Text style={THEME.textBold}>CMR Cell</Text> and plan on spending your duty day at the
            MDG on the day of your CMR appointment.
          </Text>
        )}
        {!!isRequest &&
          !isOHE &&
          !isCMR &&
          !isAnamTest &&
          !isCommunityHealthInterviewOrTravelMedicine && (
            <Text style={[THEME.textRegular, { marginTop: 25, textAlign: 'center' }]}>
              If not available, the clinic will reach out to reschedule.
            </Text>
          )}
        {!!isAnamTest && (
          <Text style={[THEME.textRegular, { marginTop: 25, textAlign: 'center' }]}>
            If anything changes, the Mental Health clinic will reach out to reschedule.
          </Text>
        )}
        {!!isCommunityHealthInterviewOrTravelMedicine && (
          <Text style={[THEME.textRegular, { marginTop: 25, textAlign: 'center' }]}>
            If anything changes, the Public Health clinic will reach out to reschedule.
          </Text>
        )}
      </>
    );
  }

  function renderSuccess(selectedDate, selectedTimes) {
    if (!selectedDate || !selectedTimes) return null;
    if (props.admin) {
      return renderSuccessAdmin(selectedDate, selectedTimes);
    } else {
      return renderSuccessPatient(selectedDate, selectedTimes, props.eventType);
    }
  }

  function renderContents() {
    let tips = props.eventType === EVENT_TYPE_PHA ? PHA_PHONE_TIP : null;
    const isPha = [EVENT_TYPE_PHA, EVENT_TYPE_PHA_FLY].includes(props.eventType);
    const isOhe = props.eventType === EVENT_TYPE_OHE;
    const isLab = props.eventType === EVENT_TYPE_LAB;
    const isImagingImport = props.eventType === EVENT_TYPE_IMAGING_IMPORT_PICKUP;
    const isSecurityForcesPCM = props.eventType === EVENT_TYPE_SECURITY_FORCES_PCM;
    const isTravis = props.base === TRAVIS;
    const isMacDill = props.base === MACDILL;
    // const isMacDill = true; // Temporarily show all MacDill fields for testing
    const isAudiogramOrSpecialtyHearing = [
      EVENT_TYPE_AUDIOGRAM,
      EVENT_TYPE_SPECIALTY_HEARING,
    ].includes(props.eventType);
    const isAudiogram = props.eventType === EVENT_TYPE_AUDIOGRAM;

    const patientRoles = useMemo(() => {
      return isMacDill && isLab
        ? [ACTIVE_DUTY]
        : isAudiogramOrSpecialtyHearing
          ? [ACTIVE_DUTY, RESERVE_DUTY, CIVILIAN_STAFF]
          : null;
    }, [isMacDill, isLab, isAudiogramOrSpecialtyHearing]);

    const requiredFields = useMemo(() => {
      return {
        ...(isOhe && { department: true }),
        ...(isLab && isMacDill && { imr: true }),
        ...(isSecurityForcesPCM && isMacDill && { appointmentReason: true }),
        ...(isAudiogram && { audiogramType: true }),
        ...(isMacDill &&
          isAudiogramOrSpecialtyHearing &&
          data?.patientRole === CIVILIAN_STAFF && { department: true }),
      };
    }, [isOhe, isLab, isMacDill, isAudiogram, isSecurityForcesPCM, data?.patientRole]);
    const omittedFields = useMemo(() => {
      const omitted = [
        'firstName',
        'lastName',
        'email',
        'dodId',
        'status',
        'serviceBranch',
        'rank',
      ].filter((field) => !!props.readOnlyData?.[field]);
      ((isPha && isTravis) || isLab || isSecurityForcesPCM) && omitted.push('department');
      return omitted;
    }, [isPha, isTravis, isLab, isSecurityForcesPCM, props.readOnlyData]);

    const optionalFields = useMemo(() => {
      const optional = [];
      if (!!isLab) optional.push('imr');
      if (!!isSecurityForcesPCM) optional.push('appointmentReason');
      if (!!isImagingImport) optional.push('upcomingApptDate');
      if (!!isAudiogram) optional.push('audiogramType');
      return optional;
    }, [isLab, isSecurityForcesPCM, isImagingImport, isAudiogram]);
    if (isMacDill && isAudiogram) {
      tips = {
        ...tips,
        ...AUDIOGRAM_TIP_MACDILL,
      };
    }

    const filteredGroups = isSecurityForcesPCM
      ? (props.groups || []).filter((group) => group.toLowerCase().match(/security forces/))
      : props.groups;

    if (submitSuccess) {
      const title =
        !props.admin && EVENT_TYPES_REQUIRING_APPROVAL.includes(props.eventType)
          ? 'Appointment Requested'
          : 'Appointment Scheduled';
      return (
        <View
          style={[
            styles.successContent,
            isVerticalLayout ? { paddingTop: 10, paddingHorizontal: 10 } : {},
          ]}
        >
          <View
            style={[
              styles.successTitleContainer,
              isVerticalLayout ? { marginBottom: 10 } : { marginBottom: 40 },
            ]}
          >
            <Image
              style={{ margin: 10 }}
              source={require('../../../../resources/icon-success-thank-you.png')}
            />
            <Text style={styles.successTitleText}>{title}</Text>
          </View>
          <View style={styles.successTextContainer}>
            {renderSuccess(props.selectedDate, props.selectedTimes)}
          </View>
        </View>
      );
    }

    return (
      <View style={{ flex: 1, zIndex: 1000, opacity: !preReqsValid ? 0.4 : 1 }}>
        {!!normalizedReadonlyData?.firstName && (
          <StaticInfo
            data={normalizedReadonlyData}
            isVerticalLayout={isVerticalLayout}
            alwaysExpanded={!!props.admin}
            disabled={!preReqsValid}
          />
        )}

        <View style={{ flex: 1, zIndex: 1000 }}>
          <PersonForm
            ref={formRef}
            tips={tips}
            requiredFields={requiredFields}
            omittedFields={omittedFields}
            optionalFields={optionalFields}
            activeDutyOnly={isPha}
            patientRoles={patientRoles}
            groups={filteredGroups}
            departments={props.departments}
            isVerticalLayout={isVerticalLayout}
            readOnly={props.readOnly}
            disabled={!preReqsValid}
            readOnlyUnlessMissing={props.readOnlyUnlessMissing}
            isMobileWeb={isVerticalLayout}
            formData={normalizedFormData}
            roleType={ROLE_TYPE_PATIENT}
            onDataChange={handleDataChange}
          />
          {!preReqsValid && (
            <View
              style={{
                // backgroundColor: COLOR.overlayLight,
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000,
              }}
            />
          )}
        </View>
      </View>
    );
  }

  function renderFooter() {
    if (props.hideButtons) return null;

    if (submitSuccess) {
      return (
        <View style={[styles.footerSuccess, isVerticalLayout ? { paddingVertical: 10 } : null]}>
          <CTAButton label="OK" onPress={cancel} />
        </View>
      );
    }
    return (
      <FormFooter
        saveLabel={props.submitBtnLabel || 'Request'}
        onSave={submit}
        onCancel={cancel}
        verticalStyle={{ paddingVertical: 5, paddingTop: 15 }}
        buttonVerticalStyle={{ marginBottom: 10 }}
        forceHorizontal={false}
        saveDisabled={
          !preReqsValid ||
          props.status === REQUESTING ||
          props.aptsRequestUpdateStatus === REQUESTING
        }
        cancelDisabled={props.status === REQUESTING || props.aptsRequestUpdateStatus === REQUESTING}
      />
    );
  }

  return (
    <Container
      scrollViewStyle={
        !preReqsValid ? { backgroundColor: 'rgb(248, 248, 248)' } : { backgroundColor: 'white' }
      }
      errorMessage={validationError || props.errorMessage?.message}
      renderActivityOverlay={
        props.status === REQUESTING || props.aptsRequestUpdateStatus === REQUESTING
      }
      scrollView={true}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="none"
      stickyFooter={true ? renderFooter() : null}
    >
      <View style={styles.header}>{renderHeader()}</View>
      {renderContents()}
      {false && renderFooter()}
    </Container>
  );
});
const styles = StyleSheet.create({
  container: {},
  headerContainer: {
    borderColor: COLOR.accent,
  },
  headerLeft: {
    flexDirection: 'row',
    paddingVertical: 20,
    justifyContent: 'center',
  },
  headerRight: {
    flexDirection: 'row',
    borderColor: COLOR.accent,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    backgroundColor: '#fcfcfc',
    borderColor: COLOR.accent,
    borderBottomWidth: 1,
  },
  footerSuccess: {
    alignItems: 'center',
    backgroundColor: '#f8f8f8',
    borderColor: COLOR.accent,
    borderTopWidth: 1,
    paddingHorizontal: 40,
    paddingVertical: 30,
  },
  successContent: {
    flex: 1,
    paddingTop: 40,
    paddingHorizontal: 30,
    paddingBottom: 10,
  },
  successTitleContainer: {
    borderRadius: 6,
    borderWidth: 1,
    borderColor: 'rgb(89, 194, 134)',
    backgroundColor: 'rgba(89, 194, 134, .2)',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  successTitleText: {
    ...FONT.medium,
    fontSize: 18,
    color: 'rgb(89, 194, 134)',
  },
  successTextContainer: {
    alignItems: 'flex-start',
    backgroundColor: 'white',
    borderColor: 'rgb(228, 228, 228)',
    borderWidth: 1,
    backgroundColor: 'rgba(228, 228, 228, .2)',
    borderRadius: 6,
    alignItems: 'center',
    paddingVertical: 50,
    paddingHorizontal: 50,
  },
  searchIcon: {
    width: 20,
    height: 20,
    tintColor: '#CCC',
    marginRight: 10,
    marginTop: 5,
  },
  lookupTxt: {
    ...FONT.regular,
    fontSize: 12,
    color: COLOR.textLight,
  },
  lookupButton: {
    ...THEME.basicRow,
    backgroundColor: COLOR.contrast,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgb(234, 234, 234)',
    height: 40,
    maxWidth: 300,
    flex: 1,
    paddingHorizontal: 10,
    marginLeft: 10,
  },
});

export default AppointmentRequestForm;
